import Alpine from 'alpinejs';

window.Alpine = Alpine;

export default {
  init() {
    // JavaScript to be fired on all pages
    console.log('👋🏻 from common.ts');

    this.initCategoryAjax();
    this.initQuoteModal();
    this.initLogoMarquee();

    Alpine.start();
  },

  initCategoryAjax() {
    // Prefetch-cache: URL → Promise<Document>
    const prefetchCache = new Map<string, Promise<Document>>();

    const fetchAndParse = (url: string): Promise<Document> => {
      if (!prefetchCache.has(url)) {
        const promise = fetch(url)
          .then((r) => {
            if (!r.ok) throw new Error('Network response was not ok');
            return r.text();
          })
          .then((html) => new DOMParser().parseFromString(html, 'text/html'))
          .catch((err) => {
            prefetchCache.delete(url);
            throw err;
          });
        prefetchCache.set(url, promise);
      }
      return prefetchCache.get(url)!;
    };

    // Prefetch vid hover/touch för snabbare navigering
    document.addEventListener('pointerenter', (e) => {
      const link = (e.target as Element)?.closest?.('.CategoryLink') as HTMLAnchorElement | null;
      if (!link?.href) return;
      // Mina sidor-navigationen ska inte prefetchas – annars GET:as t.ex.
      // logout-endpointen vid hover och kunden loggas ut oavsiktligt.
      if (link.closest('.MyAccountNavigation')) return;
      const url = link.href.split('#')[0];
      fetchAndParse(url);
    }, true);

    const loadCategory = async (url: string, scrollY: number, clickedLink: HTMLElement | null) => {
      try {
        document.body.classList.add('is-loading-category');

        // Omedelbar visuell feedback: markera klickad knapp som aktiv
        if (clickedLink) {
          const row = clickedLink.closest('.ContentSection');
          if (row) {
            row.querySelectorAll('.CategoryLink').forEach((el) => {
              el.classList.remove('is-active');
              el.classList.remove('is-parent');
            });
            row.classList.add('is-havingActiveCategoryLink');
          }
          clickedLink.classList.add('is-active');
        }

        const doc = await fetchAndParse(url);

        // 1. Uppdatera kategorinavigation
        const newNavs = doc.querySelectorAll('.CategoryNavigation');
        const currentNavs = document.querySelectorAll('.CategoryNavigation');
        currentNavs.forEach((nav, index) => {
          if (newNavs[index]) {
            nav.innerHTML = newNavs[index].innerHTML;
          }
        });

        // 2. Uppdatera produktgrid / innehåll
        const contentSelectors = ['.wp-block-query', '.products', 'main.wp-block-group', '#content'];
        for (const selector of contentSelectors) {
          const newElements = doc.querySelectorAll(selector);
          const currentElements = document.querySelectorAll(selector);
          if (newElements.length > 0 && currentElements.length > 0) {
            currentElements.forEach((el, index) => {
              if (newElements[index]) {
                el.innerHTML = newElements[index].innerHTML;
              }
            });
            break;
          }
        }

        // 3. URL, titel och sidrubriker
        window.history.pushState({}, doc.title, url);
        document.title = doc.title;
        const newTitles = doc.querySelectorAll('.wp-block-query-title');
        const currentTitles = document.querySelectorAll('.wp-block-query-title');
        currentTitles.forEach((title, index) => {
          if (newTitles[index]) {
            title.innerHTML = newTitles[index].innerHTML;
          }
        });

        // 4. Behåll scrollposition
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });

        document.dispatchEvent(new CustomEvent('category-updated', { detail: { url } }));
      } catch (error) {
        console.error('AJAX Category Load failed:', error);
        if (error instanceof Error && error.message.includes('Network')) {
          window.location.href = url;
        }
      } finally {
        document.body.classList.remove('is-loading-category');
      }
    };

    // Triggas av inline script i wp_head som redan har stoppat navigering (preventDefault)
    document.addEventListener('brainforest-category-ajax', ((e: CustomEvent<{ url: string; scrollY: number; clickedLink?: HTMLElement }>) => {
      const { url, scrollY, clickedLink } = e.detail || {};
      if (url) {
        loadCategory(url, scrollY ?? window.scrollY, clickedLink ?? null);
      }
    }) as EventListener);

    window.addEventListener('popstate', () => {
      window.location.reload();
    });
  },

  initLogoMarquee() {
    const tracks = document.querySelectorAll<HTMLElement>('.logos__track');
    if (!tracks.length) return;

    tracks.forEach((track) => {
      const viewport = track.parentElement;
      if (!viewport) return;

      // Originaluppsattningen sparas EN gang, sa upprepade korningar
      // (resize) inte bygger pa tidigare kloner.
      let originals = track.querySelectorAll<HTMLElement>(':scope > *:not([data-marquee-clone])');
      if (!originals.length) return;

      const setup = () => {
        // Rensa gamla kloner.
        track.querySelectorAll('[data-marquee-clone]').forEach((el) => el.remove());

        originals = track.querySelectorAll<HTMLElement>(':scope > *:not([data-marquee-clone])');
        if (!originals.length) return;

        // Bredden pa EN uppsattning, inklusive gapet efter sista elementet.
        // Utan det gapet hoppar loopen med gap-bredden vid varje varv.
        const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        let setWidth = 0;
        originals.forEach((el) => {
          setWidth += el.getBoundingClientRect().width + gap;
        });

        if (setWidth <= 0) return;

        // Klona tills sparet ar minst 2x synlig bredd. En halva maste
        // tacka hela fonstret, annars syns tom yta i slutet av varvet -
        // det var felet: 9 loggor gav 1172px halva pa en 1396px skarm.
        const needed = Math.max(2, Math.ceil((viewport.clientWidth * 2) / setWidth) + 1);

        for (let i = 1; i < needed; i++) {
          originals.forEach((el) => {
            const clone = el.cloneNode(true) as HTMLElement;
            clone.setAttribute('data-marquee-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            track.appendChild(clone);
          });
        }

        // Animera exakt en uppsattnings bredd i PIXLAR. translateX(-50%)
        // antog att sparet bestod av precis tva identiska halvor, vilket
        // inte haller nar antalet kloner varierar.
        track.style.setProperty('--marquee-distance', `-${setWidth}px`);

        // Hall hastigheten konstant oavsett hur mycket vi klonat:
        // ~60px/s ger samma tempo som 40s pa den ursprungliga bredden.
        const duration = Math.max(20, Math.round(setWidth / 60));
        track.style.setProperty('--marquee-duration', `${duration}s`);

        track.classList.add('logos__track--ready');
      };

      // Bilderna maste vara laddade innan vi mater bredden - annars ar
      // getBoundingClientRect() 0 och hela berakningen blir fel.
      const imgs = Array.from(track.querySelectorAll('img'));
      const pending = imgs.filter((img) => !img.complete);

      if (!pending.length) {
        setup();
      } else {
        let left = pending.length;
        pending.forEach((img) => {
          const done = () => {
            if (--left === 0) setup();
          };
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }

      // Rakna om vid resize - skarmbredden avgor hur manga kloner som behovs.
      let resizeTimer: number | undefined;
      window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(setup, 200);
      });
    });
  },

  initQuoteModal() {
    const open = () => {
      document.querySelector('.js-quote-modal')?.classList.add('is-active');
      document.body.classList.add('quote-modal-open');
    };

    // Aterstall formularet sa nasta offertforfragan borjar rent. CF7 lamnar
    // kvar sina status-klasser och sitt svar i DOM efter ett utskick.
    const resetQuoteForm = () => {
      const modal = document.querySelector('.js-quote-modal');
      if (!modal) return;

      modal.classList.remove('is-sent');

      const form = modal.querySelector('form.wpcf7-form') as HTMLFormElement | null;
      if (!form) return;

      form.classList.remove('sent', 'failed', 'aborted', 'spam', 'invalid', 'unaccepted');

      const response = modal.querySelector('.wpcf7-response-output');
      if (response) {
        response.textContent = '';
        response.className = 'wpcf7-response-output';
        response.setAttribute('aria-hidden', 'true');
        response.removeAttribute('role');
      }
    };

    const close = () => {
      document.querySelector('.js-quote-modal')?.classList.remove('is-active');
      document.body.classList.remove('quote-modal-open');

      // Aterstall EFTER att panelen glidit ut, sa kunden inte ser innehallet
      // bytas mitt i animationen.
      setTimeout(resetQuoteForm, 350);
    };

    // Move hidden product-data inputs inside CF7 <form> so they get submitted
    const productData = document.querySelector('.quote-modal__product-data');
    const cf7Form = document.querySelector('.quote-modal__form form');
    if (productData && cf7Form) {
      Array.from(productData.children).forEach((input) => cf7Form.appendChild(input));
      productData.remove();
    }

    // Event delegation – works regardless of when elements are rendered
    document.addEventListener('click', (e) => {
      const target = e.target as Element;
      if (target.closest('.js-open-quote-modal')) {
        open();
      } else if (target.closest('.js-close-quote-modal')) {
        close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.querySelector('.js-quote-modal.is-active')) {
        close();
      }
    });

    // CF7 skriver sitt svar i .wpcf7-response-output UNDER submit-knappen.
    // Modalen ar en smal panel (max-width 28rem) med overflow-y, sa svaret
    // hamnade utanfor synfaltet - kunden sag bara att falten tomdes.
    //
    // Vid LYCKAT utskick ersatter vi darfor formularet med bekraftelsen
    // (klassen is-sent, CSS doljer falt + knapp). Formularet aterstalls nar
    // modalen stangs, sa nasta forfragan borjar rent.
    document.addEventListener('wpcf7mailsent', () => {
      const modal = document.querySelector('.js-quote-modal');
      if (!modal) return;

      modal.classList.add('is-sent');

      const response = modal.querySelector('.wpcf7-response-output');
      if (response) {
        // CF7 satter aria-hidden="true" pa svaret. Nar det ersatter
        // formularet ar det sidans huvudinnehall och maste lasas upp for
        // skarmlasare.
        response.setAttribute('aria-hidden', 'false');
        response.setAttribute('role', 'status');

        requestAnimationFrame(() => {
          response.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    });

    // Vid FEL maste falten och knappen vara kvar sa kunden kan rata och
    // forsoka igen - vi scrollar bara fram meddelandet.
    ['wpcf7invalid', 'wpcf7mailfailed', 'wpcf7spam'].forEach((evt) => {
      document.addEventListener(evt, () => {
        const modal = document.querySelector('.js-quote-modal');
        if (!modal || !modal.classList.contains('is-active')) return;

        const response = modal.querySelector('.wpcf7-response-output');
        if (!response) return;

        requestAnimationFrame(() => {
          response.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    });
  },

  finalize() {
    // JavaScript to be fired on all pages, after page specific JS is fired
  },
};
